import { TraceHistory, Snapshot, SnapshotMarker } from 'types';

import { parseTraceFromBuffer } from 'utils/traceUtils';
import { MarkerHistoryForm } from '../types';
import {
  getCenterWithLo,
  getFrequencyFromSnapshotIndex,
  getIndexFromSnapshotFrequency,
} from 'utils/FreqUtils';
import { formatInTimeZone } from 'date-fns-tz';

interface TableRow {
  id?: number;
  timestamp?: string;
  marker1Amplitude: number;
  marker2Amplitude: number;
  marker3Amplitude: number;
  marker4Amplitude: number;
  marker2Delta?: string;
  marker3Delta?: string;
  marker4Delta?: string;
}

export const tableColumns = [
  {
    field: 'id',
    hide: true,
  },
  {
    field: 'timestamp',
    headerName: 'Timestamp',
    width: 200,
  },
  {
    field: 'marker1Amplitude',
    headerName: 'Marker 1',
    width: 100,
  },
  {
    field: 'marker2Amplitude',
    headerName: 'Marker 2',
    width: 100,
  },
  {
    field: 'marker3Amplitude',
    headerName: 'Marker 3',
    width: 100,
  },
  {
    field: 'marker4Amplitude',
    headerName: 'Marker 4',
    width: 100,
  },
  {
    field: 'marker2Delta',
    headerName: 'M2-M1',
    width: 100,
  },
  {
    field: 'marker3Delta',
    headerName: 'M3-M1',
    width: 100,
  },
  {
    field: 'marker4Delta',
    headerName: 'M4-M1',
    width: 100,
  },
];

export const buildTableRows = (
  traces: TraceHistory[],
  markers: SnapshotMarker[],
  timezone: string,
  timestampFormat: string
): TableRow[] => {
  let rows: TableRow[] = [];

  for (let i = 0; i < traces.length; i++) {
    const parsedTrace = parseTraceFromBuffer(traces[i].trace);
    const row = formatTableRow(parsedTrace, markers);

    rows.push({
      ...row,
      id: traces[i].id,
      timestamp: formatInTimeZone(
        new Date(traces[i].time),
        timezone,
        timestampFormat
      ),
    });
  }

  return rows;
};

const formatTableRow = (trace: number[], markers: any): TableRow => {
  let markerAmplitudes: number[] = [];

  for (let i = 0; i < markers.length; i++) {
    const markerIndex = markers[i].index;
    markerAmplitudes[markerIndex] = Number(trace[markers[i].x_pos]);
  }

  const row: TableRow = {
    marker1Amplitude: markerAmplitudes[0],
    marker2Amplitude: markerAmplitudes[1],
    marker3Amplitude: markerAmplitudes[2],
    marker4Amplitude: markerAmplitudes[3],
  };

  if (markers[0]?.x_pos) {
    if (markers[1]?.x_pos) {
      row.marker2Delta = (markerAmplitudes[1] - markerAmplitudes[0]).toFixed(2);
    }

    if (markers[2]?.x_pos) {
      row.marker3Delta = (markerAmplitudes[2] - markerAmplitudes[0]).toFixed(2);
    }

    if (markers[3]?.x_pos) {
      row.marker4Delta = (markerAmplitudes[3] - markerAmplitudes[0]).toFixed(2);
    }
  }

  return row;
};

export const getMarkersToQuery = (
  formState: MarkerHistoryForm,
  snapshot: Snapshot
) => {
  const markersToQuery: number[] = [];

  for (let i = 1; i < 5; i++) {
    const marker =
      formState[
        ('marker' + i) as 'marker1' | 'marker2' | 'marker3' | 'marker4'
      ];
    if (marker) {
      markersToQuery.push(
        getIndexFromSnapshotFrequency(
          snapshot.center,
          snapshot.span,
          601,
          `${marker.value} ${marker.frequency}`,
          snapshot.lo,
          snapshot.lo_mode
        )
      );
    }
  }
  return markersToQuery;
};

export const getFrequencyAndValueFromSnapshotMarker = (
  snapshotMarker: SnapshotMarker,
  snapshot: Snapshot
) => {
  const { x_pos, index } = snapshotMarker;
  const frequency = getFrequencyFromSnapshotIndex(
    snapshot.center,
    snapshot.span,
    601,
    x_pos
  );
  const freqWithLo = getCenterWithLo(frequency, snapshot.lo, snapshot.lo_mode);
  const splitFreq = freqWithLo.split(' ');
  return {
    index,
    value: Number(splitFreq[0]).toFixed(2),
    frequency: splitFreq[1],
  };
};
